import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  type GrowthWheelUserForReview,
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelAssessmentSessionsByUserIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
  useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery,
} from '../../redux/growth-wheel.endpoints'
import { useGetAllRolesQuery } from '../../redux/role.endpoints'
import {
  setGrowthWheelData,
  setUserCompetenciesData,
  setLoadingGrowthWheel,
  setCurrentCompetency,
  setCurrentSubCompetency,
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setAssessmentCompletion,
  setAssessmentSession,
  setAssessmentSessions,
  setSelectedAssessmentSessionId,
  setActiveAssessmentSessionId,
  setActiveGrowthWheelId,
  setCurrentAreasOfFocus,
  setIsReadOnly,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { getGrowthWheelRoleId } from '../../types/Roles'
import { User } from '../../types/User'
import { AdminContributorList } from '../../components/GrowthWheelComponents/AdminContributorList'
import BackButton from '../../components/GrowthWheelComponents/BackButton'
import GrowthWheelDashboard from '../GrowthWheelDashboard/GrowthWheelDashboard'
import GrowthWheelCompetencyAreaView from '../GrowthWheelCompetencyView/GrowthWheelCompetencyView'
import GrowthWheelCompetencyAssessment from '../GrowthWheelSubCompetencyView/GrowthWheelSubCompetencyView'

const GrowthWheelAdmin = ({ user }: { user: User }) => {
  const dispatch = useDispatch()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const { data: contributorsData } =
    useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery(
      { reviewerId: user.id },
      { refetchOnMountOrArgChange: true },
    )

  const selectedUser = contributorsData?.find(c => c.id === selectedUserId)
  const { data: guilds } = useGetAllRolesQuery()

  const roleId =
    selectedUserId != null
      ? getGrowthWheelRoleId(selectedUser ?? user, guilds)
      : user.guildId
  const isResolvingGuildMasterRole =
    Boolean(selectedUser?.isGuildMaster) && !guilds

  const { data: growthWheelData, isLoading } = useGetGrowthWheelByRoleIdQuery(
    { roleId },
    {
      skip: !selectedUserId || isResolvingGuildMasterRole,
      refetchOnMountOrArgChange: true,
    },
  )
  const { data: assessmentSessionsData } =
    useGetGrowthWheelAssessmentSessionsByUserIdQuery(
      { userId: selectedUserId ?? 0 },
      { skip: !selectedUserId, refetchOnMountOrArgChange: true },
    )
  const assessmentSessions = useMemo(
    () => assessmentSessionsData ?? [],
    [assessmentSessionsData],
  )
  const activeSessionId = useMemo(
    () =>
      assessmentSessions.find(
        session =>
          session.status === 'in_progress' &&
          session.growthWheelStatus === 'active',
      )?.id ?? null,
    [assessmentSessions],
  )
  const selectedSessionId = useSelector(
    (state: RootState) => state.growthWheel.selectedAssessmentSessionId,
  )
  const viewingSessionId =
    selectedSessionId ?? activeSessionId ?? assessmentSessions[0]?.id ?? null
  const { data: userAssessmentData } =
    useGetGrowthWheelWithEvaluationsByUserIdQuery(
      {
        userId: selectedUserId ?? 0,
        assessmentSessionId: viewingSessionId,
      },
      { skip: !selectedUserId, refetchOnMountOrArgChange: true },
    )
  const userCompetenciesData = userAssessmentData?.competencies
  const viewingSession =
    assessmentSessions.find(session => session.id === viewingSessionId) ?? null
  const isViewingReadOnly =
    viewingSession?.status === 'completed' ||
    (viewingSessionId != null && viewingSessionId !== activeSessionId)

  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )
  const currentSubCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentSubCompetency,
  )

  useEffect(() => {
    dispatch(
      setGrowthWheelData(
        userAssessmentData?.growthWheel ?? growthWheelData ?? null,
      ),
    )
  }, [dispatch, growthWheelData, userAssessmentData])

  useEffect(() => {
    dispatch(setAssessmentSessions(assessmentSessions))
    dispatch(setActiveAssessmentSessionId(activeSessionId))
    dispatch(setActiveGrowthWheelId(growthWheelData?.id ?? null))
  }, [activeSessionId, assessmentSessions, dispatch, growthWheelData])

  useEffect(() => {
    dispatch(setUserCompetenciesData(userCompetenciesData ?? []))
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(evaluateGrowthWheelCompletion())
    dispatch(setCurrentAreasOfFocus())
    dispatch(
      setAssessmentCompletion({
        isCompleted: userAssessmentData?.isCompleted ?? false,
        completedAt: userAssessmentData?.completedAt ?? null,
      }),
    )
    dispatch(
      setAssessmentSession(userAssessmentData?.assessmentSession ?? null),
    )
    dispatch(setIsReadOnly(Boolean(isViewingReadOnly)))
  }, [dispatch, userCompetenciesData, userAssessmentData, isViewingReadOnly])

  useEffect(() => {
    dispatch(setLoadingGrowthWheel(isLoading || isResolvingGuildMasterRole))
  }, [dispatch, isLoading, isResolvingGuildMasterRole])

  useEffect(() => {
    if (!userCompetenciesData?.length) return
    if (currentCompetency) {
      const freshCompetency = userCompetenciesData.find(
        c => c.id === currentCompetency.id,
      )
      if (freshCompetency) {
        dispatch(setCurrentCompetency(freshCompetency))
        if (currentSubCompetency) {
          const freshSub = freshCompetency.subCompetencies?.find(
            s => s.id === currentSubCompetency.id,
          )
          if (freshSub) dispatch(setCurrentSubCompetency(freshSub))
        }
      }
    }
  }, [userCompetenciesData, currentCompetency, currentSubCompetency, dispatch])

  useEffect(() => {
    if (
      selectedSessionId != null &&
      !assessmentSessions.some(session => session.id === selectedSessionId)
    ) {
      dispatch(setSelectedAssessmentSessionId(null))
    }
  }, [assessmentSessions, dispatch, selectedSessionId])

  const handleSelectContributor = (contributor: GrowthWheelUserForReview) => {
    setSelectedUserId(contributor.id)
    dispatch(setSelectedAssessmentSessionId(null))
    dispatch(setCurrentCompetency(null))
    dispatch(setCurrentSubCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBackToContributors = () => {
    setSelectedUserId(null)
    dispatch(setSelectedAssessmentSessionId(null))
    dispatch(setCurrentCompetency(null))
    dispatch(setCurrentSubCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const displayAssessmentView = currentSubCompetency && currentCompetency
  const displayCompetencyView = currentCompetency && !currentSubCompetency
  const displayDashboardView = !currentCompetency && growthWheelData

  const reviewedUserName = selectedUser
    ? `${selectedUser.firstName} ${selectedUser.lastName}`
    : ''
  const reviewedGuildName = selectedUser?.area ?? ''

  return (
    <main>
      {!selectedUserId ? (
        <AdminContributorList
          contributors={contributorsData ?? []}
          onSelectContributor={handleSelectContributor}
        />
      ) : (
        <div className="container mb-5 gw-growth-wheel-container">
          {selectedUserId && !currentCompetency && !currentSubCompetency && (
            <div className="my-5">
              <BackButton
                onClick={handleBackToContributors}
                text="Back to team members"
              />
            </div>
          )}

          {displayAssessmentView && selectedUserId != null && (
            <GrowthWheelCompetencyAssessment
              subjectUserId={selectedUserId}
              reviewerUserId={user.id}
              isAdminView={user.isAdmin || user.isGrowthWheelReviewer}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
          {displayCompetencyView && (
            <GrowthWheelCompetencyAreaView
              subjectUserId={selectedUserId}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
          {displayDashboardView && (
            <GrowthWheelDashboard
              isAdminView={user.isAdmin || user.isGrowthWheelReviewer}
              userId={selectedUserId}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
        </div>
      )}
    </main>
  )
}

export default GrowthWheelAdmin
